import { useState, useEffect, useCallback, useRef } from "react";
import { MediaItem, MediaItemData } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { ReviewStage } from "@/domains/LibraryView/reviewStage";
import { apiClient } from "@/services/apiClient";
import { UserRole } from "@/services/urlService";

interface UseReviewQueueProps {
  clientId: string;
  stage?: ReviewStage;
  contentType: "videos" | "photos" | "reels";
  isOpen: boolean;
  editorMode?: boolean;
  editorToken?: string;
  initialMediaId?: number;
}

interface ReviewQueueState {
  items: MediaItem[];
  currentIndex: number;
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
}

// Incremental fetching constants
const BATCH_SIZE = 4; // Current + 3 ahead
const PRELOAD_THRESHOLD = 2; // Fetch when within 2 of end
const LOAD_MORE_SIZE = 2; // Fetch 2 at a time when navigating

export const useReviewQueue = ({
  clientId,
  stage,
  contentType,
  isOpen,
  editorMode = false,
  editorToken,
  initialMediaId,
}: UseReviewQueueProps) => {
  const [state, setState] = useState<ReviewQueueState>({
    items: [],
    currentIndex: 0,
    isLoading: false,
    error: null,
    total: 0,
    hasMore: false,
  });

  const [hasChanges, setHasChanges] = useState(false);
  const isLoadingMore = useRef(false);
  const lastFetchedOffset = useRef(0);

  // Fetch initial batch when modal opens
  useEffect(() => {
    if (!isOpen || !clientId) return;
    if (!editorMode && !stage) return; // Admin mode requires stage

    const fetchInitialItems = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        let response: {
          videos?: unknown[];
          photos?: unknown[];
          mediaItems?: unknown[];
          total: number;
          offset: number;
          limit: number;
          hasMore: boolean;
          stage?: string;
        };

        if (editorMode) {
          // Editor mode: use editor endpoint with JWT auth
          const headers: Record<string, string> = {};
          if (editorToken) {
            headers["Authorization"] = `Bearer ${editorToken}`;
          }

          const mediaType = contentType === "photos" ? "images" : "videos";
          const result = await apiClient.get<{
            success: boolean;
            mediaItems: unknown[];
            totalCount: number;
            offset: number;
            limit: number;
            hasMore: boolean;
          }>(
            `/editor/${clientId}/media?media_type=${mediaType}&offset=0&limit=${BATCH_SIZE}`,
            { headers }
          );

          response = {
            mediaItems: result.mediaItems,
            total: result.totalCount,
            offset: result.offset,
            limit: result.limit,
            hasMore: result.hasMore,
          };
        } else {
          // Admin mode: use admin endpoint with Google OAuth
          let endpoint = `/client/${clientId}/media/review-queue?stage=${stage}&content_type=${contentType}&limit=${BATCH_SIZE}`;

          // Add start_from_id if we have an initial media ID
          if (initialMediaId) {
            endpoint += `&start_from_id=${initialMediaId}`;
          } else {
            endpoint += `&offset=0`;
          }

          response = await apiClient.get<{
            videos?: unknown[];
            photos?: unknown[];
            total: number;
            offset: number;
            limit: number;
            hasMore: boolean;
            stage: string;
          }>(endpoint);
        }

        const itemsData =
          response.videos || response.photos || response.mediaItems || [];
        const items = (itemsData as MediaItemData[]).map((itemData) =>
          MediaItem.fromBackend(
            itemData,
            editorMode ? UserRole.EDITOR : UserRole.ADMIN,
            clientId,
            editorToken
          )
        );

        // Track the offset for subsequent fetches
        lastFetchedOffset.current = response.offset + items.length;

        setState({
          items,
          currentIndex: 0, // Always start at index 0 since backend returns items from the correct position
          isLoading: false,
          error: null,
          total: response.total,
          hasMore: response.hasMore,
        });
      } catch (err) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            err instanceof Error ? err.message : `Failed to load ${contentType}`,
        }));
      }
    };

    fetchInitialItems();
  }, [
    isOpen,
    clientId,
    stage,
    contentType,
    editorMode,
    editorToken,
    initialMediaId,
  ]);

  // Load more items when approaching the end
  const loadMoreItems = useCallback(async () => {
    if (isLoadingMore.current || !state.hasMore) return;

    isLoadingMore.current = true;

    try {
      let response: {
        videos?: unknown[];
        photos?: unknown[];
        mediaItems?: unknown[];
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
        stage?: string;
      };

      if (editorMode) {
        // Editor mode: use editor endpoint with JWT auth
        const headers: Record<string, string> = {};
        if (editorToken) {
          headers["Authorization"] = `Bearer ${editorToken}`;
        }

        const mediaType = contentType === "photos" ? "images" : "videos";
        const result = await apiClient.get<{
          success: boolean;
          mediaItems: unknown[];
          totalCount: number;
          offset: number;
          limit: number;
          hasMore: boolean;
        }>(
          `/editor/${clientId}/media?media_type=${mediaType}&offset=${lastFetchedOffset.current}&limit=${LOAD_MORE_SIZE}`,
          { headers }
        );

        response = {
          mediaItems: result.mediaItems,
          total: result.totalCount,
          offset: result.offset,
          limit: result.limit,
          hasMore: result.hasMore,
        };
      } else {
        // Admin mode: use admin endpoint with Google OAuth
        const endpoint = `/client/${clientId}/media/review-queue?stage=${stage}&content_type=${contentType}&offset=${lastFetchedOffset.current}&limit=${LOAD_MORE_SIZE}`;

        response = await apiClient.get<{
          videos?: unknown[];
          photos?: unknown[];
          total: number;
          offset: number;
          limit: number;
          hasMore: boolean;
          stage: string;
        }>(endpoint);
      }

      const itemsData =
        response.videos || response.photos || response.mediaItems || [];
      const newItems = (itemsData as MediaItemData[]).map((itemData) =>
        MediaItem.fromBackend(
          itemData,
          editorMode ? UserRole.EDITOR : UserRole.ADMIN,
          clientId,
          editorToken
        )
      );

      // Update the offset tracker
      lastFetchedOffset.current += newItems.length;

      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...newItems],
        hasMore: response.hasMore,
      }));
    } catch (err) {
      console.error(`Failed to load more ${contentType}:`, err);
    } finally {
      isLoadingMore.current = false;
    }
  }, [
    clientId,
    stage,
    contentType,
    state.hasMore,
    editorMode,
    editorToken,
  ]);

  // Check if we need to load more based on current index
  useEffect(() => {
    if (!isOpen || !state.hasMore) return;

    const shouldLoadMore =
      state.currentIndex >= state.items.length - PRELOAD_THRESHOLD;

    if (shouldLoadMore) {
      loadMoreItems();
    }
  }, [
    state.currentIndex,
    state.items.length,
    state.hasMore,
    isOpen,
    loadMoreItems,
  ]);

  const currentItem = state.items[state.currentIndex] || null;

  const navigateNext = useCallback(() => {
    setState((prev) => {
      const maxIndex = prev.items.length - 1;
      if (prev.currentIndex < maxIndex) {
        return { ...prev, currentIndex: prev.currentIndex + 1 };
      }
      return prev;
    });
  }, []);

  const navigatePrev = useCallback(() => {
    setState((prev) => {
      if (prev.currentIndex > 0) {
        return { ...prev, currentIndex: prev.currentIndex - 1 };
      }
      return prev;
    });
  }, []);

  const canNavigateNext = state.currentIndex < state.items.length - 1 || state.hasMore;
  const canNavigatePrev = state.currentIndex > 0;

  const updateItemStage = useCallback(
    async (newStage: MediaStage) => {
      if (!currentItem) return;

      const previousStage = currentItem.stage;

      // Optimistic update
      setState((prev) => {
        const updatedItems = [...prev.items];
        const itemIndex = prev.currentIndex;
        const updatedItem = updatedItems[itemIndex].withStage(newStage);
        updatedItems[itemIndex] = updatedItem;

        return { ...prev, items: updatedItems };
      });

      setHasChanges(true);

      try {
        await apiClient.patch(`/client/${clientId}/media/${currentItem.id}/stage`, {
          stage: newStage,
        });
      } catch (err) {
        // Revert on error
        setState((prev) => {
          const revertedItems = [...prev.items];
          const itemIndex = prev.currentIndex;
          const revertedItem = revertedItems[itemIndex].withStage(
            previousStage || MediaStage.DRAFT
          );
          revertedItems[itemIndex] = revertedItem;

          return { ...prev, items: revertedItems };
        });

        throw err;
      }
    },
    [currentItem, clientId]
  );

  // Sync a saved website-tags edit back into the in-memory queue item so
  // navigating away and back reflects the change without a full reload.
  const updateItemTags = useCallback(
    (
      itemId: number,
      tags: Array<{ tag: string; properties: Record<string, string> }>
    ) => {
      setState((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === itemId ? item.withWebsiteTags(tags) : item
        ),
      }));
    },
    []
  );

  const downloadCurrentItem = useCallback(
    async (
      onProgress?: (
        status: "checking" | "downloading" | "ready" | "failed"
      ) => void
    ) => {
      if (!currentItem) return;

      try {
        const context = editorMode ? "editor" : "admin";
        await currentItem.downloadFile(context, onProgress);
      } catch (err) {
        console.error(`Failed to download ${contentType.slice(0, -1)}:`, err);
        throw err;
      }
    },
    [currentItem, editorMode, contentType]
  );

  return {
    items: state.items,
    currentItem,
    currentIndex: state.currentIndex,
    total: state.total,
    isLoading: state.isLoading,
    error: state.error,
    navigateNext,
    navigatePrev,
    canNavigateNext,
    canNavigatePrev,
    updateItemStage,
    updateItemTags,
    downloadCurrentItem,
    hasChanges,
  };
};
